import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import ProcedureReportService from '@/services-http/servicos/ProcedureReportService';
import { store as observableStore } from '@/shared/observable/store';
import store from '@/store/index';
import i18n from '@/plugins/i18n';

import {
  buildBatchPayload,
  buildCorrectRulePayload,
  buildFilterParams,
  buildProcedurePayload,
  defaultFilter,
  defaultPagination,
  initState,
  mergeCorrectedProcedureRecord,
  normalizeApproveResponse,
  normalizeClassification,
  normalizeHistory,
  normalizeOptionsPage,
  normalizeProcedure,
  normalizeProcedureOptions,
  normalizeProceduresPage,
} from './procedure-report-factory';
import {
  CorrectClassificationRulePayload,
  Modal,
  ProcedureAppliedRule,
  ProcedureReportFilter,
  ProcedureReportHistoryItem,
  ProcedureReportItem,
  ProcedureReportModel,
  ProcedureReportOption,
  ProcedureReportPagination,
  TableOptions,
} from './procedure-report-types';

type OptionKey = 'carrierOptions'
  | 'procedureCodeOptions'
  | 'procedureDescriptionOptions'
  | 'tussCodeOptions'
  | 'eventOptions'
  | 'complementOptions'
  | 'groupOptions'
  | 'subGroupOptions'
  | 'predictiveOptions'
  | 'predictiveClassificationOptions';

type FetchOptionsPayload = {
  page?: number;
  size?: number;
  inputValue?: string;
  eventIds?: Array<number | string>;
  groupIds?: Array<number | string>;
  predictiveIds?: Array<number | string>;
};

type NewDimensionField = 'eventId'
  | 'complementId'
  | 'groupId'
  | 'subGroupId'
  | 'predictiveId'
  | 'predictiveClassificationId';

type SearchPayload = boolean | {
  append: boolean;
  skipClassification?: boolean;
};

type DimensionResolution = {
  value: unknown;
  option?: ProcedureReportOption;
};

const serializeUniqueIds = (ids: Array<number | string> = []) => Array.from(
  new Set(ids.filter((id) => id !== null && id !== undefined && id !== '').map(String)),
).join(',');

const procedureSortFields: Record<string, string> = {
  carrierId: 'carrier.carrierId',
  procedureCodeId: 'procedureCode',
  procedureDescriptionId: 'procedureDescription',
  tussCodeId: 'tussCode',
  subGroupId: 'subgroup.description',
  eventId: 'event.description',
  complementId: 'complement.description',
  groupId: 'group.description',
  predictiveId: 'predictive.description',
  predictiveClassificationId: 'predictiveClassification.description',
};

let activeProcedureSaves = 0;
let previousCustomDialogState = false;
let activeProcedureSearches = 0;
let previousSearchCustomDialogState = false;
let activeClassificationRequests = 0;
let previousClassificationCustomDialogState = false;
let activeClassificationFetches = 0;
let procedureSearchRequestSequence = 0;
let classificationRequestSequence = 0;

const runProcedureSearch = async <T>(request: () => Promise<T>): Promise<T> => {
  if (activeProcedureSearches === 0) {
    previousSearchCustomDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
  }
  activeProcedureSearches += 1;

  try {
    return await request();
  } finally {
    activeProcedureSearches -= 1;
    if (activeProcedureSearches === 0) {
      observableStore.isCustomDialog = previousSearchCustomDialogState;
    }
  }
};

const runProcedureClassification = async <T>(request: () => Promise<T>): Promise<T> => {
  if (activeClassificationRequests === 0) {
    previousClassificationCustomDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
  }
  activeClassificationRequests += 1;

  try {
    return await request();
  } finally {
    activeClassificationRequests -= 1;
    if (activeClassificationRequests === 0) {
      observableStore.isCustomDialog = previousClassificationCustomDialogState;
    }
  }
};

const runProcedureSave = async <T>(request: () => Promise<T>): Promise<T> => {
  if (activeProcedureSaves === 0) {
    previousCustomDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
  }
  activeProcedureSaves += 1;

  try {
    return await request();
  } finally {
    activeProcedureSaves -= 1;
    if (activeProcedureSaves === 0) {
      observableStore.isCustomDialog = previousCustomDialogState;
    }
  }
};

const newDimensionFields: Array<{
  field: NewDimensionField;
  serviceMethod: string;
  optionKey: OptionKey;
}> = [
  { field: 'eventId', serviceMethod: 'createEvent', optionKey: 'eventOptions' },
  { field: 'complementId', serviceMethod: 'createComplement', optionKey: 'complementOptions' },
  { field: 'groupId', serviceMethod: 'createGroup', optionKey: 'groupOptions' },
  { field: 'subGroupId', serviceMethod: 'createSubGroup', optionKey: 'subGroupOptions' },
  { field: 'predictiveId', serviceMethod: 'createPredictive', optionKey: 'predictiveOptions' },
  {
    field: 'predictiveClassificationId',
    serviceMethod: 'createPredictiveClassification',
    optionKey: 'predictiveClassificationOptions',
  },
];

const findNewDimensionField = (field: keyof ProcedureReportItem) => (
  newDimensionFields.find((dimension) => dimension.field === field)
);

const hasValue = (value: unknown) => (
  value !== null
  && value !== undefined
  && (typeof value !== 'string' || Boolean(value.trim()))
);

const findExistingDimension = (
  options: ProcedureReportOption[],
  value: string,
) => {
  const normalizedValue = value.trim().toLowerCase();
  return options.find((option) => (
    String(option.id) === value
    || option.name.trim().toLowerCase() === normalizedValue
  ));
};

const buildCreatedDimensionOption = (
  payload: any,
  fallbackId: number | string,
  fallbackName: string,
): ProcedureReportOption => ({
  id: payload?.id ?? fallbackId,
  name: String(payload?.description ?? payload?.name ?? fallbackName).trim(),
});

const normalizeDimensionId = (value: unknown): number | string | null => {
  if (!hasValue(value)) return null;
  if (typeof value === 'number') return value;

  const normalizedValue = String(value).trim();
  const numberValue = Number(normalizedValue);
  return Number.isNaN(numberValue) ? normalizedValue : numberValue;
};

const buildDimensionPayload = (
  field: NewDimensionField,
  value: string,
  record: Partial<ProcedureReportItem> = {},
) => {
  const payload: Record<string, any> = {
    description: value.trim(),
    active: true,
  };

  if (field === 'complementId') {
    const eventId = normalizeDimensionId(record.eventId);
    if (eventId === null) throw new Error('Event is required to create complement');
    return { ...payload, eventId };
  }

  if (field === 'subGroupId') {
    const groupId = normalizeDimensionId(record.groupId);
    if (groupId === null) throw new Error('Group is required to create subgroup');
    return { ...payload, groupIds: [groupId] };
  }

  if (field === 'predictiveClassificationId') {
    const predictiveId = normalizeDimensionId(record.predictiveId);
    if (predictiveId === null) {
      throw new Error('Predictive is required to create predictive classification');
    }
    return { ...payload, predictiveIds: [predictiveId] };
  }

  return payload;
};

const createNewDimension = async (
  service: ProcedureReportService,
  field: keyof ProcedureReportItem,
  value: unknown,
  optionsByKey: Partial<Record<OptionKey, ProcedureReportOption[]>>,
  record: Partial<ProcedureReportItem> = {},
): Promise<DimensionResolution> => {
  const dimension = findNewDimensionField(field);
  if (!dimension || typeof value !== 'string' || !value.trim()) return { value };

  const existingOption = findExistingDimension(optionsByKey[dimension.optionKey] || [], value);
  if (existingOption) return { value: existingOption.id, option: existingOption };

  const response = await (service as any)[dimension.serviceMethod](
    buildDimensionPayload(dimension.field, value, record),
  );
  const createdId = response?.data?.id;
  if (createdId === null || createdId === undefined) {
    throw new Error(`Could not create dimension for ${field}`);
  }

  return {
    value: createdId,
    option: buildCreatedDimensionOption(response?.data, createdId, value),
  };
};

const createNewDimensions = async <T extends Partial<ProcedureReportItem>>(
  service: ProcedureReportService,
  record: T,
  optionsByKey: Partial<Record<OptionKey, ProcedureReportOption[]>>,
): Promise<T> => {
  const resolvedRecord = { ...record } as T;

  return newDimensionFields.reduce<Promise<T>>(async (recordPromise, { field, optionKey }) => {
    const currentRecord = await recordPromise;
    const resolution = await createNewDimension(
      service,
      field,
      (currentRecord as ProcedureReportItem)[field],
      { [optionKey]: optionsByKey[optionKey] },
      currentRecord,
    );
    if (resolution.value !== (currentRecord as ProcedureReportItem)[field]) {
      (currentRecord as ProcedureReportItem)[field] = resolution.value as number | string | null;
    }
    return currentRecord;
  }, Promise.resolve(resolvedRecord));
};

@Module({
  namespaced: true,
  name: 'procedure-report',
  dynamic: true,
  store,
})
class ProcedureReportStore extends VuexModule {
  data: ProcedureReportModel = initState;

  get filter() { return this.data.filter; }

  get records() { return this.data.records; }

  get loadingTable() { return this.data.loadingTable; }

  get loadingTableAppend() { return this.data.loadingTableAppend; }

  get loadingExport() { return this.data.loadingExport; }

  get loadingUpdate() { return this.data.loadingUpdate; }

  get loadingClassification() { return this.data.loadingClassification; }

  get loadingRecordHistories() { return this.data.loadingRecordHistories; }

  get loadingApproval() { return this.data.loadingApproval; }

  get loadingCorrectRule() { return this.data.loadingCorrectRule; }

  get clickAway() { return this.data.clickAway; }

  get pagination() { return this.data.pagination; }

  get currentPage() { return this.data.pagination.page; }

  get itemsPerPage() { return this.data.pagination.itemsPerPage; }

  get totalItems() { return this.data.pagination.totalItems; }

  get sortBy() { return this.data.sortBy; }

  get sortDesc() { return this.data.sortDesc; }

  get procedureReportsSelecteds() { return this.data.procedureReportsSelecteds; }

  get classification() { return this.data.classification; }

  get carrierOptions() { return this.data.carrierOptions; }

  get procedureCodeOptions() { return this.data.procedureCodeOptions; }

  get procedureDescriptionOptions() { return this.data.procedureDescriptionOptions; }

  get tussCodeOptions() { return this.data.tussCodeOptions; }

  get eventOptions() { return this.data.eventOptions; }

  get complementOptions() { return this.data.complementOptions; }

  get groupOptions() { return this.data.groupOptions; }

  get subGroupOptions() { return this.data.subGroupOptions; }

  get predictiveOptions() { return this.data.predictiveOptions; }

  get predictiveClassificationOptions() { return this.data.predictiveClassificationOptions; }

  get recordHistories() { return this.data.recordHistories; }

  get showHistoryModal() { return this.data.showHistoryModal; }

  get showSnackbarCustomize() { return this.data.showSnackbarCustomize; }

  get justificationModal() { return this.data.justificationModal; }

  get hasFiltersSelected() {
    const f = this.data.filter;
    return f.carrierId.length > 0
      || Boolean(f.procedureCodeId)
      || Boolean(f.procedureDescriptionId)
      || Boolean(f.tussCodeId)
      || f.eventId.length > 0
      || f.complementId.length > 0
      || f.groupId.length > 0
      || f.subGroupId.length > 0
      || f.predictiveId.length > 0
      || f.predictiveClassificationId.length > 0
      || f.status.length > 0;
  }

  @Action
  async initPage() {
    this.RESET_FILTER_STATE();
    this.CLEAR_RESULTS();
    await this.searchByFilter();
  }

  @Action
  applyFilter(filter: Partial<ProcedureReportFilter>) {
    this.SET_FILTER(filter);
  }

  @Action
  resetFilter() {
    this.RESET_FILTER_STATE();
    this.CLEAR_RESULTS();
  }

  @Action
  clearResults() {
    this.CLEAR_RESULTS();
  }

  @Action
  async searchByFilter(options: { skipClassification?: boolean } = {}) {
    this.SET_PAGINATION({ page: 1 });
    await this.search({
      append: false,
      skipClassification: Boolean(options.skipClassification),
    });
  }

  @Action
  async fetchClassification(filters?: Record<string, any>) {
    classificationRequestSequence += 1;
    const requestId = classificationRequestSequence;

    if (activeClassificationFetches === 0) {
      this.SET_CLASSIFICATION_LOADING(true);
    }
    activeClassificationFetches += 1;

    try {
      const params = {
        ...buildFilterParams(this.data.filter),
        ...(filters ?? {}),
      };
      delete params.statuses;

      const response = await runProcedureClassification(async () => {
        const service = new ProcedureReportService();
        return service.fetchClassification(params);
      });

      if (requestId === classificationRequestSequence) {
        this.SET_CLASSIFICATION(normalizeClassification(response?.data ?? response));
      }
      return true;
    } catch {
      if (requestId === classificationRequestSequence) {
        this.showSnackbar({
          type: 'error',
          message: String(i18n.t('procedureReport.messages.classificationError')),
          show: true,
        });
      }
      return false;
    } finally {
      activeClassificationFetches -= 1;
      if (activeClassificationFetches === 0) {
        this.SET_CLASSIFICATION_LOADING(false);
      }
    }
  }

  @Action
  async updateTableOptions(options: TableOptions) {
    const nextItemsPerPage = options?.itemsPerPage ?? this.data.pagination.itemsPerPage;
    const nextSortBy = Array.isArray(options?.sortBy) && options.sortBy.length > 0
      ? options.sortBy[0]
      : this.data.sortBy;
    const nextSortDesc = Array.isArray(options?.sortDesc) && options.sortDesc.length > 0
      ? Boolean(options.sortDesc[0])
      : this.data.sortDesc;
    const sortingChanged = nextSortBy !== this.data.sortBy || nextSortDesc !== this.data.sortDesc;
    const nextPage = sortingChanged ? 1 : options?.page ?? this.data.pagination.page;
    const hasChanged = nextPage !== this.data.pagination.page
      || nextItemsPerPage !== this.data.pagination.itemsPerPage
      || sortingChanged;

    if (!hasChanged) return;

    this.SET_PAGINATION({ page: nextPage, itemsPerPage: nextItemsPerPage });
    this.SET_SORTING({ sortBy: nextSortBy, sortDesc: Boolean(nextSortDesc) });
    await this.search({
      append: !sortingChanged && nextPage > 1,
      skipClassification: sortingChanged,
    });
  }

  @Action
  async exportProcedures() {
    const service = new ProcedureReportService();
    try {
      this.SET_EXPORT_LOADING(true);
      await service.export(buildFilterParams(this.data.filter));
      return true;
    } catch {
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t('procedureReport.messages.exportError')),
        show: true,
      });
      return false;
    } finally {
      this.SET_EXPORT_LOADING(false);
    }
  }

  @Action
  updateRecordField(payload: { id: ProcedureReportItem['id']; field: keyof ProcedureReportItem; value: any }) {
    this.UPDATE_RECORD_FIELD(payload);
  }

  @Action
  addNewRecord() {
    this.PREPEND_RECORD({
      id: `new-${Date.now()}`,
      isNew: true,
      carrierId: this.data.filter.carrierId[0] ?? null,
      procedureCodeId: null,
      procedureDescriptionId: null,
      tussCodeId: null,
      eventId: null,
      groupId: null,
      subGroupId: null,
      complementId: null,
      predictiveId: null,
      predictiveClassificationId: null,
      additionalTussCode: null,
      updateDescription: false,
      active: true,
      status: 'NAO_CLASSIFICADO',
      editedBy: null,
      editedAt: null,
    });
  }

  @Action
  discardNewRecord(id: ProcedureReportItem['id']) {
    const record = this.data.records.find((item) => String(item.id) === String(id));
    if (!record?.isNew) return false;

    this.REMOVE_RECORD(id);
    return true;
  }

  @Action
  async saveRecord(record: ProcedureReportItem) {
    if (record.isNew) return this.createRecord(record);
    return this.updateRecord(record);
  }

  @Action
  async createRecordDimension(payload: {
    field: keyof ProcedureReportItem;
    value: unknown;
    record?: Partial<ProcedureReportItem>;
  }) {
    const dimension = findNewDimensionField(payload.field);
    if (!dimension) return { value: payload.value, error: null };

    try {
      const service = new ProcedureReportService();
      const resolution = await runProcedureSave(() => createNewDimension(
        service,
        payload.field,
        payload.value,
        {
          eventOptions: this.data.eventOptions,
          complementOptions: this.data.complementOptions,
          groupOptions: this.data.groupOptions,
          subGroupOptions: this.data.subGroupOptions,
          predictiveOptions: this.data.predictiveOptions,
          predictiveClassificationOptions: this.data.predictiveClassificationOptions,
        },
        payload.record,
      ));

      if (resolution.option && resolution.value !== payload.value) {
        this.MERGE_OPTIONS({
          key: dimension.optionKey,
          options: [resolution.option],
        });
      }
      return { value: resolution.value, option: resolution.option, error: null };
    } catch (error) {
      return { value: payload.value, error };
    }
  }

  @Action
  async createRecord(record: ProcedureReportItem) {
    try {
      const service = new ProcedureReportService();
      let resolvedRecord = record;
      const response = await runProcedureSave(
        async () => {
          resolvedRecord = await createNewDimensions(service, record, {
            eventOptions: this.data.eventOptions,
            complementOptions: this.data.complementOptions,
            groupOptions: this.data.groupOptions,
            subGroupOptions: this.data.subGroupOptions,
            predictiveOptions: this.data.predictiveOptions,
            predictiveClassificationOptions: this.data.predictiveClassificationOptions,
          });
          return service.createProcedure(buildProcedurePayload(resolvedRecord));
        },
      );
      const savedRecord = response?.data ? normalizeProcedure(response.data) : { ...resolvedRecord, isNew: false };
      this.REPLACE_RECORD({ currentId: record.id, record: savedRecord });
      if (response?.data) this.MERGE_PROCEDURE_OPTIONS(normalizeProcedureOptions([response.data]));
      this.fetchClassification();
      return response;
    } catch (error) {
      return error;
    }
  }

  @Action
  async updateRecord(record: ProcedureReportItem) {
    try {
      const service = new ProcedureReportService();
      let resolvedRecord = record;
      const response = await runProcedureSave(
        async () => {
          resolvedRecord = await createNewDimensions(service, record, {
            eventOptions: this.data.eventOptions,
            complementOptions: this.data.complementOptions,
            groupOptions: this.data.groupOptions,
            subGroupOptions: this.data.subGroupOptions,
            predictiveOptions: this.data.predictiveOptions,
            predictiveClassificationOptions: this.data.predictiveClassificationOptions,
          });
          return service.updateProcedure(record.id, buildProcedurePayload(resolvedRecord));
        },
      );
      this.UPDATE_RECORD(response?.data ? normalizeProcedure(response.data) : resolvedRecord);
      this.fetchClassification();
      return response;
    } catch (error) {
      return error;
    }
  }

  @Action
  async bulkUpdateProcedures(form: Partial<ProcedureReportItem>) {
    if (!this.data.procedureReportsSelecteds.length) return false;

    try {
      this.SET_LOADING_UPDATE(true);
      const service = new ProcedureReportService();
      const resolvedForm = await createNewDimensions(service, form, {
        eventOptions: this.data.eventOptions,
        complementOptions: this.data.complementOptions,
        groupOptions: this.data.groupOptions,
        subGroupOptions: this.data.subGroupOptions,
        predictiveOptions: this.data.predictiveOptions,
        predictiveClassificationOptions: this.data.predictiveClassificationOptions,
      });
      const payload = buildBatchPayload(this.data.procedureReportsSelecteds, resolvedForm);
      const response = await service.updateProceduresBatch(payload);
      const updated = response?.data && Array.isArray(response.data)
        ? response.data.map(normalizeProcedure)
        : payload.map(normalizeProcedure);
      updated.forEach((record) => this.UPDATE_RECORD(record));
      this.SET_PROCEDURE_REPORTS_SELECTEDS([]);
      this.fetchClassification();
      return response?.status ?? 200;
    } catch (error) {
      return error?.response?.status ?? false;
    } finally {
      this.SET_LOADING_UPDATE(false);
    }
  }

  @Action
  async approveProcedures(ids: Array<number | string>) {
    const numericIds = ids
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    if (!numericIds.length) return false;

    try {
      this.SET_LOADING_APPROVAL(true);
      const service = new ProcedureReportService();
      const response = await service.approveClassifications({ ids: numericIds });
      const {
        approvedRecords,
        approvedCount,
        ignoredIds,
      } = normalizeApproveResponse(response, numericIds);

      const approvedIdSet = new Set(
        approvedRecords.length > 0
          ? approvedRecords.map((row: ProcedureReportItem) => Number(row.id)).filter((id: number) => !Number.isNaN(id))
          : numericIds.filter((id) => !ignoredIds.includes(id)),
      );

      approvedRecords.forEach((row: ProcedureReportItem) => {
        this.UPDATE_RECORD(row);
      });

      if (approvedRecords.length === 0 && approvedIdSet.size > 0) {
        approvedIdSet.forEach((approvedId) => {
          const record = this.data.records.find((item) => Number(item.id) === approvedId);
          if (!record) return;
          this.UPDATE_RECORD({ ...record, status: 'CLASSIFICADO' });
        });
      }

      this.SET_PROCEDURE_REPORTS_SELECTEDS(
        this.data.procedureReportsSelecteds.filter(
          (record) => !approvedIdSet.has(Number(record.id)),
        ),
      );

      if (approvedCount > 0) {
        await this.search({ append: false, skipClassification: true });
      }

      void this.fetchClassification();

      const approved = approvedCount;
      const ignored = numericIds.filter((id) => !approvedIdSet.has(id)).length;

      if (approved > 0 || ignored > 0) {
        const snackbarType = approved > 0 ? 'success' : 'warning';
        this.showSnackbar({
          type: snackbarType,
          message: String(i18n.t('procedureReport.messages.approveSuccess', { approved, ignored })),
          show: true,
        });
      }
      return true;
    } catch (error) {
      const backendMessage = error?.response?.data?.message ?? error?.message;
      this.showSnackbar({
        type: 'error',
        message: backendMessage
          ? String(backendMessage)
          : String(i18n.t('procedureReport.messages.approveError')),
        show: true,
      });
      return false;
    } finally {
      this.SET_LOADING_APPROVAL(false);
    }
  }

  @Action
  async correctClassificationRule(payload: CorrectClassificationRulePayload) {
    try {
      this.SET_LOADING_CORRECT_RULE(true);
      const response = await runProcedureSave(async () => {
        const service = new ProcedureReportService();
        return service.correctClassification(buildCorrectRulePayload(payload));
      });
      const responseData = response?.data ?? response ?? {};
      const previousRecord = this.data.justificationModal.record
        ?? this.data.records.find((record) => String(record.id) === String(payload.procedureId))
        ?? null;
      const updatedRecord = mergeCorrectedProcedureRecord(
        previousRecord,
        normalizeProcedure(responseData),
        payload,
        responseData?.createdRule ?? responseData?.rule ?? null,
      );
      this.UPDATE_RECORD(updatedRecord);
      this.SET_JUSTIFICATION_MODAL({ show: false, record: null, editingRule: null });
      await this.search({ append: false, skipClassification: true });
      void this.fetchClassification();
      this.showSnackbar({
        type: 'success',
        message: String(i18n.t('procedureReport.messages.correctRuleSuccess')),
        show: true,
      });
      return updatedRecord;
    } catch (error) {
      const backendMessage = error?.response?.data?.message ?? error?.message;
      this.showSnackbar({
        type: 'error',
        message: backendMessage
          ? String(backendMessage)
          : String(i18n.t('procedureReport.messages.correctRuleError')),
        show: true,
      });
      return null;
    } finally {
      this.SET_LOADING_CORRECT_RULE(false);
    }
  }

  @Action
  openJustificationModal(record: ProcedureReportItem) {
    this.SET_JUSTIFICATION_MODAL({ show: true, record, editingRule: null });
  }

  @Action
  closeJustificationModal() {
    this.SET_JUSTIFICATION_MODAL({ show: false, record: null, editingRule: null });
  }

  @Action
  setJustificationEditingRule(editingRule: ProcedureAppliedRule | null) {
    this.SET_JUSTIFICATION_MODAL({
      ...this.data.justificationModal,
      editingRule,
    });
  }

  @Action
  async fetchProcedureHistory(recordId: number | string) {
    const previousHistoryDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
    try {
      this.SET_LOADING_RECORD_HISTORIES(true);
      const service = new ProcedureReportService();
      const response = await service.fetchProcedureHistory(recordId);
      this.SET_RECORD_HISTORIES(normalizeHistory(response?.data));
      this.showHistory({ show: true });
    } catch {
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t('procedureReport.messages.historyError')),
        show: true,
      });
    } finally {
      this.SET_LOADING_RECORD_HISTORIES(false);
      observableStore.isCustomDialog = previousHistoryDialogState;
    }
  }

  @Action
  async search(payload: SearchPayload) {
    const append = typeof payload === 'boolean' ? payload : payload.append;
    const skipClassification = typeof payload === 'boolean'
      ? false
      : Boolean(payload.skipClassification);
    procedureSearchRequestSequence += 1;
    const requestId = procedureSearchRequestSequence;
    const filters = { ...buildFilterParams(this.data.filter) };

    this.SET_TABLE_LOADING(true);
    this.SET_TABLE_LOADING_APPEND(append);
    if (!append) this.CLEAR_TABLE_RESULTS();

    let shouldFetchClassification = false;
    let searchFailed = false;

    try {
      await runProcedureSearch(async () => {
        const service = new ProcedureReportService();
        const response = await service.fetchProcedures(filters, {
          page: this.data.pagination.page - 1,
          size: this.data.pagination.itemsPerPage,
          sort: this.data.sortBy
            ? `${procedureSortFields[this.data.sortBy] ?? this.data.sortBy},${this.data.sortDesc ? 'desc' : 'asc'}`
            : undefined,
        });
        if (requestId !== procedureSearchRequestSequence) return;

        const page = normalizeProceduresPage(response);
        this.SET_TOTAL_ITEMS(page.totalElements);
        this.SET_RECORDS(page.content);
        this.MERGE_PROCEDURE_OPTIONS(normalizeProcedureOptions(response));
        shouldFetchClassification = !append && !skipClassification;
      });
    } catch {
      if (requestId !== procedureSearchRequestSequence) return;

      searchFailed = true;
      if (!append) this.CLEAR_TABLE_RESULTS();
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t('procedureReport.messages.searchError')),
        show: true,
      });
    } finally {
      if (requestId === procedureSearchRequestSequence) {
        this.SET_TABLE_LOADING(false);
        this.SET_TABLE_LOADING_APPEND(false);
      }
    }

    if (requestId !== procedureSearchRequestSequence || searchFailed) return;

    if (shouldFetchClassification) {
      await this.fetchClassification(filters);
    }
  }

  @Action
  async fetchCarrierOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchDomainOptions({
      optionKey: 'carrierOptions',
      serviceMethod: 'fetchCarriers',
      payload,
      requestParams: {
        financialGroupIds: [],
        ...(payload.inputValue ? { name: payload.inputValue } : {}),
      },
      valueKeys: ['id', 'carrierId', 'code'],
      textKeys: ['name', 'description', 'code'],
    });
  }

  @Action
  async fetchEventOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchSimpleDomain({
      optionKey: 'eventOptions',
      serviceMethod: 'fetchEvents',
      payload,
      activeField: 'active',
    });
  }

  @Action
  async fetchComplementOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchSimpleDomain({
      optionKey: 'complementOptions',
      serviceMethod: 'fetchComplements',
      payload,
      activeField: 'active',
      requestParams: { eventIds: serializeUniqueIds(payload.eventIds) },
    });
  }

  @Action
  async fetchGroupOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchSimpleDomain({
      optionKey: 'groupOptions',
      serviceMethod: 'fetchGroups',
      payload,
      activeField: 'isActive',
    });
  }

  @Action
  async fetchSubGroupOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchSimpleDomain({
      optionKey: 'subGroupOptions',
      serviceMethod: 'fetchSubGroups',
      payload,
      activeField: 'isActive',
      requestParams: { groupIds: serializeUniqueIds(payload.groupIds) },
    });
  }

  @Action
  async fetchPredictiveOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchSimpleDomain({
      optionKey: 'predictiveOptions',
      serviceMethod: 'fetchPredictives',
      payload,
      activeField: 'isActive',
    });
  }

  @Action
  async fetchPredictiveClassificationOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchSimpleDomain({
      optionKey: 'predictiveClassificationOptions',
      serviceMethod: 'fetchPredictiveClassifications',
      payload,
      activeField: 'isActive',
      requestParams: { predictiveIds: serializeUniqueIds(payload.predictiveIds) },
    });
  }

  @Action
  async fetchProcedureCodeOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchProcedureDerivedOptions('procedureCodeOptions', payload, ['procedureCode', 'procedureCodeId', 'code']);
  }

  @Action
  async fetchProcedureDescriptionOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchProcedureDerivedOptions(
      'procedureDescriptionOptions',
      payload,
      ['procedureDescription', 'procedureDescriptionId', 'description'],
    );
  }

  @Action
  async fetchTussCodeOptions(payload: FetchOptionsPayload = {}) {
    return this.fetchProcedureDerivedOptions('tussCodeOptions', payload, ['tussCode', 'tussCodeId', 'tuss']);
  }

  @Action
  private async fetchSimpleDomain(config: {
    optionKey: OptionKey;
    serviceMethod: string;
    payload: FetchOptionsPayload;
    activeField: 'active' | 'isActive';
    requestParams?: Record<string, any>;
  }) {
    return this.fetchDomainOptions({
      optionKey: config.optionKey,
      serviceMethod: config.serviceMethod,
      payload: config.payload,
      requestParams: {
        [config.activeField]: true,
        ...(config.requestParams || {}),
        ...(config.payload.inputValue ? { description: config.payload.inputValue } : {}),
      },
      valueKeys: ['id', 'code', 'description'],
      textKeys: ['description', 'name', 'code'],
    });
  }

  @Action
  private async fetchDomainOptions(config: {
    optionKey: OptionKey;
    serviceMethod: string;
    payload: FetchOptionsPayload;
    requestParams: Record<string, any>;
    valueKeys: string[];
    textKeys: string[];
  }) {
    const service = new ProcedureReportService();
    const response = await (service as any)[config.serviceMethod]({
      ...config.requestParams,
      page: config.payload.page ?? 0,
      size: config.payload.size ?? 20,
    });
    const page = normalizeOptionsPage(response, config.valueKeys, config.textKeys);
    this.MERGE_OPTIONS({ key: config.optionKey, options: page.content });
    return page;
  }

  @Action
  private async fetchProcedureDerivedOptions(
    optionKey: OptionKey,
    payload: FetchOptionsPayload,
    keys: string[],
  ) {
    const service = new ProcedureReportService();
    const response = await service.fetchProcedures({ ...(payload.inputValue ? { [keys[0]]: payload.inputValue } : {}) }, {
      page: payload.page ?? 0,
      size: payload.size ?? 20,
    });
    const page = normalizeOptionsPage(response, keys, keys);
    const normalizedInput = String(payload.inputValue ?? '').toLowerCase();
    const content = normalizedInput
      ? page.content.filter((option) => option.name.toLowerCase().includes(normalizedInput))
      : page.content;
    this.MERGE_OPTIONS({ key: optionKey, options: content });
    return { ...page, content };
  }

  @Mutation
  SET_PROCEDURE_REPORTS_SELECTEDS(reportsSelecteds: ProcedureReportItem[]) {
    this.data.procedureReportsSelecteds = reportsSelecteds;
  }

  @Mutation
  private SET_TABLE_LOADING(loading: boolean) { this.data.loadingTable = loading; }

  @Mutation
  private SET_TABLE_LOADING_APPEND(loading: boolean) { this.data.loadingTableAppend = loading; }

  @Mutation
  private SET_EXPORT_LOADING(loading: boolean) { this.data.loadingExport = loading; }

  @Mutation
  private SET_LOADING_UPDATE(loading: boolean) { this.data.loadingUpdate = loading; }

  @Mutation
  private SET_CLASSIFICATION_LOADING(loading: boolean) { this.data.loadingClassification = loading; }

  @Mutation
  private SET_LOADING_RECORD_HISTORIES(loading: boolean) { this.data.loadingRecordHistories = loading; }

  @Mutation
  private SET_LOADING_APPROVAL(loading: boolean) { this.data.loadingApproval = loading; }

  @Mutation
  private SET_LOADING_CORRECT_RULE(loading: boolean) { this.data.loadingCorrectRule = loading; }

  @Mutation
  private SET_JUSTIFICATION_MODAL(payload: {
    show: boolean;
    record: ProcedureReportItem | null;
    editingRule?: ProcedureAppliedRule | null;
  }) {
    this.data.justificationModal = {
      show: payload.show,
      record: payload.record,
      editingRule: payload.editingRule ?? null,
    };
  }

  @Mutation
  private SET_FILTER(filter: Partial<ProcedureReportFilter>) {
    this.data.filter = { ...this.data.filter, ...filter };
  }

  @Mutation
  private RESET_FILTER_STATE() { this.data.filter = defaultFilter(); }

  @Mutation
  private SET_PAGINATION(pagination: Partial<ProcedureReportPagination>) {
    this.data.pagination = { ...this.data.pagination, ...pagination };
  }

  @Mutation
  private SET_SORTING({ sortBy, sortDesc }: { sortBy: string | null; sortDesc: boolean }) {
    this.data.sortBy = sortBy;
    this.data.sortDesc = sortDesc;
  }

  @Mutation
  private CLEAR_RECORDS() { this.data.records = []; }

  @Mutation
  private CLEAR_TABLE_RESULTS() {
    this.data.records = [];
    this.data.procedureReportsSelecteds = [];
    this.data.pagination = { ...this.data.pagination, totalItems: 0 };
  }

  @Mutation
  private CLEAR_RESULTS() {
    this.data.records = [];
    this.data.procedureReportsSelecteds = [];
    this.data.pagination = defaultPagination();
    this.data.classification = normalizeClassification(null);
  }

  @Mutation
  private SET_RECORDS(records: ProcedureReportItem[]) {
    const existingIds = new Set(this.data.records.map((record) => String(record.id)));
    this.data.records = [
      ...this.data.records,
      ...records.filter((record) => !existingIds.has(String(record.id))),
    ];
  }

  @Mutation
  private PREPEND_RECORD(record: ProcedureReportItem) { this.data.records = [record, ...this.data.records]; }

  @Mutation
  private REMOVE_RECORD(id: ProcedureReportItem['id']) {
    this.data.records = this.data.records.filter((record) => String(record.id) !== String(id));
    this.data.procedureReportsSelecteds = this.data.procedureReportsSelecteds
      .filter((record) => String(record.id) !== String(id));
  }

  @Mutation
  private REPLACE_RECORD(payload: { currentId: ProcedureReportItem['id']; record: ProcedureReportItem }) {
    const index = this.data.records.findIndex((record) => record.id === payload.currentId);
    if (index === -1) return;

    if (
      this.data.filter.status.length > 0
      && payload.record.status
      && !this.data.filter.status.includes(payload.record.status)
    ) {
      this.data.records.splice(index, 1);
      this.data.procedureReportsSelecteds = this.data.procedureReportsSelecteds
        .filter((selected) => String(selected.id) !== String(payload.record.id));
      return;
    }

    this.data.records.splice(index, 1, payload.record);
  }

  @Mutation
  private SET_RECORD_HISTORIES(histories: ProcedureReportHistoryItem[]) {
    this.data.recordHistories = histories;
  }

  @Mutation
  private SET_TOTAL_ITEMS(total: number) { this.data.pagination.totalItems = total; }

  @Mutation
  private SET_CLASSIFICATION(classification: ProcedureReportModel['classification']) {
    this.data.classification = classification;
  }

  @Mutation
  private UPDATE_RECORD_FIELD(payload: { id: ProcedureReportItem['id']; field: keyof ProcedureReportItem; value: any }) {
    const index = this.data.records.findIndex((record) => record.id === payload.id);
    if (index !== -1) this.data.records.splice(index, 1, { ...this.data.records[index], [payload.field]: payload.value });
  }

  @Mutation
  private UPDATE_RECORD(record: ProcedureReportItem) {
    const index = this.data.records.findIndex((existing) => existing.id === record.id);
    if (index === -1) return;

    if (
      this.data.filter.status.length > 0
      && record.status
      && !this.data.filter.status.includes(record.status)
    ) {
      this.data.records.splice(index, 1);
      this.data.procedureReportsSelecteds = this.data.procedureReportsSelecteds
        .filter((selected) => String(selected.id) !== String(record.id));
      return;
    }

    this.data.records.splice(index, 1, record);
  }

  @Mutation
  private MERGE_OPTIONS(payload: { key: OptionKey; options: ProcedureReportOption[] }) {
    const current = this.data[payload.key] as ProcedureReportOption[];
    const map = new Map(current.map((option) => [String(option.id), option]));
    payload.options.forEach((option) => map.set(String(option.id), option));
    this.data[payload.key] = Array.from(map.values()) as any;
  }

  @Mutation
  private MERGE_PROCEDURE_OPTIONS(optionsByKey: Record<string, ProcedureReportOption[]>) {
    Object.entries(optionsByKey).forEach(([key, options]) => {
      const optionKey = key as OptionKey;
      const current = new Map(
        (this.data[optionKey] as ProcedureReportOption[]).map((option) => [String(option.id), option]),
      );
      options.forEach((option) => current.set(String(option.id), option));
      this.data[optionKey] = Array.from(current.values()) as any;
    });
  }

  @Mutation
  resetRecordHistories() { this.data.recordHistories = []; }

  @Mutation
  showSnackbar(modalParams: Modal) {
    this.data.showSnackbarCustomize = modalParams;

    if (modalParams.show) {
      setTimeout(() => {
        this.data.showSnackbarCustomize = {
          type: null,
          top: null,
          message: '',
          show: false,
        };
      }, 5000);
    }
  }

  @Mutation
  showHistory(modalParams: Modal) { this.data.showHistoryModal = modalParams; }

  @Mutation
  onClickAway() {
    this.data.clickAway = true;
    setTimeout(() => { this.data.clickAway = false; }, 1000);
  }
}

export default getModule(ProcedureReportStore);
